import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { formatBeijingDateTime } from '@/temu-platform/utils/formatBeijingDateTime';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function formatJson(value) {
  if (value == null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * 运费模板详情（只读）：平台接口仅返回概要，展示本地同步缓存。
 */
export default function FreightTemplateDetailModal({
  open = false,
  shopId,
  templateId,
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !shopId || !templateId) {
      setDetail(null);
      setError('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setDetail(null);
      try {
        const res = await temuPlatformAPI.getShopFreightTemplate(shopId, templateId);
        if (!cancelled) setDetail(res || null);
      } catch (e) {
        if (!cancelled) {
          setDetail(null);
          setError(e?.message || t('freightLib.detailLoadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, shopId, templateId, t]);

  if (!open) return null;

  const payloadText = detail?.payload ? formatJson(detail.payload) : '';

  const copyPayload = async () => {
    const text = payloadText || error || '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showToast(t('freightLib.copied'), 'success');
    } catch {
      showToast(t('freightLib.copyFailed'), 'error');
    }
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-json-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={onClose}
      />
      <div
        className="tpo-batch-modal__panel tpo-cert-json-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-freight-detail-title"
      >
        <div className="tpo-batch-modal__head">
          <div>
            <h2 id="tpo-freight-detail-title">{t('freightLib.detail')}</h2>
            <p className="tpo-batch-modal__hint" style={{ margin: '4px 0 0' }}>
              {detail?.detailHint
                || t('freightLib.detailSummary')}
            </p>
          </div>
          <button type="button" className="tpo-batch-modal__close" onClick={onClose} aria-label={t('moduleShell.close')}>
            ×
          </button>
        </div>

        <div className="tpo-batch-modal__body tpo-cert-json-modal__body">
          {loading ? (
            <p className="tpo-batch-modal__hint">{t('returnList.loading')}</p>
          ) : error ? (
            <p className="tpo-batch-modal__hint">{error}</p>
          ) : detail ? (
            <>
              <dl className="tpo-freight-detail__meta">
                <div>
                  <dt>{t('freightLib.colId')}</dt>
                  <dd>{detail.freightTemplateId || detail.id || '—'}</dd>
                </div>
                <div>
                  <dt>{t('productTemplate.colName')}</dt>
                  <dd>{detail.templateName || detail.name || '—'}</dd>
                </div>
                <div>
                  <dt>{t('freightLib.colSynced')}</dt>
                  <dd>{formatBeijingDateTime(detail.lastSyncedAt) || '—'}</dd>
                </div>
              </dl>
              <p className="tpo-batch-modal__hint" style={{ margin: '12px 0 6px' }}>{t('freightLib.rawPayload')}</p>
              {payloadText ? (
                <pre className="tpo-cert-json-modal__pre">{payloadText}</pre>
              ) : (
                <p className="tpo-batch-modal__hint">{t('freightLib.noPayload')}</p>
              )}
            </>
          ) : (
            <p className="tpo-batch-modal__hint">{t('freightLib.noData')}</p>
          )}
        </div>

        <div className="tpo-batch-modal__foot">
          <button
            type="button"
            className="tpo-btn tpo-btn--secondary"
            disabled={!payloadText}
            onClick={copyPayload}
          >
            {t('freightLib.copyJson')}
          </button>
          <button type="button" className="tpo-btn tpo-btn--primary" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
